// Conversion du HTML d'un bloc édité dans l'aperçu (contenteditable) vers l'`InlineText`
// du contrat (`document-model.ts`) : **gras**, *italique*, [libellé](url), [S1].
// Tout le reste est du texte brut. Le HTML vient d'une iframe non fiable : on le lit
// avec un tokenizer minimal (jamais injecté dans le DOM) et on ne conserve que les
// liens http(s)/mailto.

type Run = { bold: boolean; href: string | null; italic: boolean; text: string };

type Frame = {
  bold: boolean;
  citeBuffer: string | null;
  citeKey: string | null;
  href: string | null;
  italic: boolean;
  tag: string;
};

const TOKEN_PATTERN = /<!--[\s\S]*?-->|<(\/?)([a-zA-Z][a-zA-Z0-9-]*)((?:"[^"]*"|'[^']*'|[^'">])*)>|[^<]+|</g;
const VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'source', 'track', 'wbr']);
const SEPARATOR_TAGS = new Set([
  'address', 'article', 'aside', 'blockquote', 'br', 'dd', 'div', 'dl', 'dt', 'figcaption', 'figure',
  'footer', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'li', 'ol', 'p', 'section', 'table',
  'td', 'th', 'tr', 'ul',
]);
const DROPPED_CONTENT_TAGS = new Set(['script', 'style', 'template', 'noscript']);
const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);
const SOURCE_KEY_PATTERN = /^S[0-9]{1,3}$/;
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', apos: "'", gt: '>', hellip: '…', laquo: '«', lt: '<', mdash: '—', nbsp: ' ',
  ndash: '–', quot: '"', raquo: '»', rsquo: '’',
};

function decodeEntities(value: string) {
  return value.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (match, entity: string) => {
    if (entity[0] === '#') {
      const code = entity[1].toLowerCase() === 'x'
        ? Number.parseInt(entity.slice(2), 16)
        : Number.parseInt(entity.slice(1), 10);
      return Number.isInteger(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
    }
    return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
  });
}

function readAttributes(raw: string) {
  const attributes = new Map<string, string>();
  const pattern = /([^\s"'<>/=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
  for (const match of raw.matchAll(pattern)) {
    attributes.set(match[1].toLowerCase(), decodeEntities(match[2] ?? match[3] ?? match[4] ?? ''));
  }
  return attributes;
}

function resolveSafeHref(value: string | undefined) {
  const href = value?.trim();
  if (!href) {
    return null;
  }
  try {
    if (!SAFE_LINK_PROTOCOLS.has(new URL(href).protocol)) {
      return null;
    }
    // `)` terminerait la cible du lien dans la syntaxe inline : on l'encode, avec les
    // caractères qui casseraient l'analyse (espaces, chevrons). L'URL saisie est conservée telle quelle.
    return href.replace(/[\s()<>]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`);
  } catch {
    return null;
  }
}

function resolveCiteKey(attributes: Map<string, string>) {
  for (const name of ['data-cite', 'data-source-key', 'data-source']) {
    const value = attributes.get(name)?.trim().replace(/^\[|\]$/g, '');
    if (value && SOURCE_KEY_PATTERN.test(value)) {
      return value;
    }
  }
  return null;
}

function isCiteElement(attributes: Map<string, string>) {
  if (resolveCiteKey(attributes) || attributes.has('data-cite')) {
    return true;
  }
  return /(^|[\s_-])cit(e|ation)($|[\s_-])/i.test(attributes.get('class') ?? '');
}

function applyInlineStyle(frame: Frame, style: string | undefined) {
  if (!style) {
    return;
  }
  const weight = /font-weight\s*:\s*([a-z0-9]+)/i.exec(style)?.[1]?.toLowerCase();
  if (weight === 'bold' || weight === 'bolder' || (weight && /^[6-9]00$/.test(weight))) {
    frame.bold = true;
  } else if (weight === 'normal' || weight === 'lighter' || (weight && /^[1-5]00$/.test(weight))) {
    frame.bold = false;
  }
  const fontStyle = /font-style\s*:\s*([a-z]+)/i.exec(style)?.[1]?.toLowerCase();
  if (fontStyle === 'italic' || fontStyle === 'oblique') {
    frame.italic = true;
  } else if (fontStyle === 'normal') {
    frame.italic = false;
  }
}

function openFrame(parent: Frame, tag: string, attributes: Map<string, string>): Frame {
  const frame: Frame = { ...parent, citeBuffer: null, citeKey: null, tag };
  if (tag === 'b' || tag === 'strong') {
    frame.bold = true;
  }
  if (tag === 'i' || tag === 'em') {
    frame.italic = true;
  }
  if (tag === 'a') {
    frame.href = resolveSafeHref(attributes.get('href'));
  }
  applyInlineStyle(frame, attributes.get('style'));
  if (isCiteElement(attributes)) {
    frame.citeBuffer = '';
    frame.citeKey = resolveCiteKey(attributes);
  }
  return frame;
}

function collectRuns(html: string): Run[] {
  const runs: Run[] = [];
  const root: Frame = { bold: false, citeBuffer: null, citeKey: null, href: null, italic: false, tag: '#root' };
  const stack: Frame[] = [root];
  let droppedDepth = 0;

  const emit = (text: string, frame: Frame) => {
    if (text) {
      runs.push({ bold: frame.bold, href: frame.href, italic: frame.italic, text });
    }
  };
  const closeFrame = (frame: Frame, parent: Frame) => {
    if (frame.citeBuffer === null) {
      return;
    }
    const key = frame.citeKey ?? /S[0-9]{1,3}/.exec(frame.citeBuffer)?.[0] ?? null;
    if (key) {
      runs.push({ bold: false, href: null, italic: false, text: `[${key}]` });
    } else {
      emit(frame.citeBuffer, parent);
    }
  };

  for (const match of html.matchAll(TOKEN_PATTERN)) {
    const [token, closing, rawTag, rawAttributes] = match;
    if (token.startsWith('<!--')) {
      continue;
    }
    if (!rawTag) {
      if (droppedDepth > 0) {
        continue;
      }
      const text = decodeEntities(token);
      const top = stack[stack.length - 1];
      const citeFrame = [...stack].reverse().find((frame) => frame.citeBuffer !== null);
      if (citeFrame) {
        citeFrame.citeBuffer = `${citeFrame.citeBuffer ?? ''}${text}`;
      } else {
        emit(text, top);
      }
      continue;
    }

    const tag = rawTag.toLowerCase();
    if (DROPPED_CONTENT_TAGS.has(tag)) {
      droppedDepth = closing ? Math.max(0, droppedDepth - 1) : droppedDepth + 1;
      continue;
    }
    if (droppedDepth > 0) {
      continue;
    }
    if (SEPARATOR_TAGS.has(tag)) {
      emit(' ', stack[stack.length - 1]);
    }
    if (VOID_TAGS.has(tag)) {
      continue;
    }
    if (!closing) {
      stack.push(openFrame(stack[stack.length - 1], tag, readAttributes(rawAttributes ?? '')));
      continue;
    }
    // Fermeture : on dépile jusqu'au tag correspondant ; une fermeture orpheline est ignorée.
    const index = stack.map((frame) => frame.tag).lastIndexOf(tag);
    if (index > 0) {
      while (stack.length > index) {
        const closed = stack.pop();
        if (closed) {
          closeFrame(closed, stack[stack.length - 1]);
        }
      }
    }
  }
  while (stack.length > 1) {
    const closed = stack.pop();
    if (closed) {
      closeFrame(closed, stack[stack.length - 1]);
    }
  }
  return runs;
}

function mergeRuns(runs: Run[]) {
  const merged: Run[] = [];
  for (const run of runs) {
    const previous = merged[merged.length - 1];
    if (previous && previous.bold === run.bold && previous.italic === run.italic && previous.href === run.href) {
      previous.text += run.text;
    } else {
      merged.push({ ...run });
    }
  }
  return merged;
}

function escapeLiteral(text: string) {
  return text.replace(/\\(?=[\\*])/g, '\\\\').replace(/\*/g, '\\*');
}

function serializeRun(run: Run) {
  const lead = /^\s*/.exec(run.text)?.[0] ?? '';
  const trail = run.text.length > lead.length ? (/\s*$/.exec(run.text)?.[0] ?? '') : '';
  const core = run.text.slice(lead.length, run.text.length - trail.length);
  if (!core) {
    return run.text;
  }
  if (run.href) {
    // Libellé en texte brut : le style d'un lien n'est pas exprimable de façon fiable.
    const label = escapeLiteral(core).replace(/\[/g, '(').replace(/\]/g, ')');
    return `${lead}[${label}](${run.href})${trail}`;
  }
  const escaped = escapeLiteral(core);
  const marker = `${run.bold ? '**' : ''}${run.italic ? '*' : ''}`;
  const closing = `${run.italic ? '*' : ''}${run.bold ? '**' : ''}`;
  return `${lead}${marker}${escaped}${closing}${trail}`;
}

const WHITESPACE_PATTERN = /[\s\u00a0\u200b]+/g;

export function htmlToInlineText(html: string): string {
  // Fusion d'abord, normalisation ensuite : les séparateurs de blocs voisins se replient en un espace.
  const merged = mergeRuns(collectRuns(html)).map((run) => ({
    ...run,
    text: run.text.replace(WHITESPACE_PATTERN, ' '),
  }));
  // Plus d'espaces doublés entre runs, ni en tête/queue du texte.
  let previousEndsWithSpace = true;
  const normalized = merged.map((run) => {
    const text = previousEndsWithSpace ? run.text.replace(/^ /, '') : run.text;
    if (text) {
      previousEndsWithSpace = text.endsWith(' ');
    }
    return { ...run, text };
  }).filter((run) => run.text.length > 0);
  const last = normalized[normalized.length - 1];
  if (last) {
    last.text = last.text.replace(/ $/, '');
  }
  return normalized.map(serializeRun).join('').trim();
}
